/**
 * #4110: recovery policy for a failed day-items load during initial sync.
 *
 * During boot, while the user database is still doing its initial sync, reads route to the
 * remote CouchDB (the pass-through mechanism, see design-docs/pass-through-db.md). The home
 * page's day-items load is one such read, so a brief network blip can make exactly that one
 * read throw (for example `TypeError: Failed to fetch`) even though the sync itself finishes
 * fine a moment later. Without recovery, the Daily Log then renders empty and stays empty
 * until the user leaves the page or restarts the app.
 *
 * This module holds the two pure pieces of the recovery, extracted from cs-day-items so they
 * can be unit-tested without Angular (same pattern as cs-day-items-update-card.ts):
 *
 *   - decideDayItemsLoadFailure: given the error, decide whether the component logs it and
 *     defers to the recovery, or rethrows it, and whether it arms the one-shot deferred reload.
 *   - runDeferredDayItemsReloadAsync: the one-shot reload itself — wait until the initial
 *     sync settles, then clear the backlog cache and reload the schedule from local data.
 *
 * #6162: a dropped request is NOT reported to Sentry when it happens. Nothing is known to be
 * wrong at that point, and every such report was noise (Sentry CYBERNETIC-FITNESS-APP-49R).
 * The recovery reports its own result instead: a recovered Daily Log as info, and a reload
 * that failed again (the Daily Log stays empty) as a problem.
 */

import { isTransientFetchError } from '../../services/error-patterns';
import { isDatabaseTornDown } from '../../services/database-unavailable-error';
import {
  InitialSyncSettledSubscribe,
  InitialSyncWaitFor,
  waitForInitialSyncAsync,
} from '../../services/user-db.task-status';

/** what one call of the component's schedule load ended with */
export enum DayItemsLoadOutcome {
  /** the Daily Log was built */
  Loaded = 'loaded',
  /** a dropped request stopped the load; the Daily Log was not built, and the deferred reload covers it */
  DeferredAfterDroppedRequest = 'deferred-after-dropped-request',
  /**
   * only ever answered to the deferred reload: a newer load (the user changed day) started
   * after the reload's own and has not finished, so the reload's own result says nothing
   * about the Daily Log on screen
   */
  NewerLoadInProgress = 'newer-load-in-progress',
}

/** what the component does with a failed schedule load */
export enum DayItemsLoadFailureAction {
  /** an unexpected error: rethrow it so it surfaces */
  Rethrow = 'rethrow',
  /** a dropped request: write a console line, no report, and leave it to the deferred reload */
  LogAndDefer = 'log-and-defer',
}

export interface DayItemsLoadFailureDecision {
  action: DayItemsLoadFailureAction;
  /** if true, the component starts runDeferredDayItemsReloadAsync (guarded so only one runs at a time) */
  armRecovery: boolean;
}

/**
 * Decides how a failed schedule load is handled: log-and-defer for a transient network
 * error (arming the one-shot deferred reload unless one is already pending), or rethrow
 * for anything unexpected.
 */
export function decideDayItemsLoadFailure(err: unknown, recoveryAlreadyArmed: boolean): DayItemsLoadFailureDecision {
  // if the error is not a transient network failure, it is unexpected — rethrow so it surfaces
  if (!isTransientFetchError(err)) {
    return { action: DayItemsLoadFailureAction.Rethrow, armRecovery: false };
  }
  // a transient failure is logged, not reported (#6162), and recovered by the deferred
  // reload, which reports its own result. arm at most one recovery at a time: if the armed retry itself fails
  // transiently, re-arming here would loop.
  //
  // deliberately no "is the pass-through window still open" gate: in the CI trace the sync
  // completed 0.25s after the failed read, so checking the window state races the sync. if
  // the sync is already done, the recovery's wait is answered straight away and it degrades
  // to one immediate local retry — harmless.
  return { action: DayItemsLoadFailureAction.LogAndDefer, armRecovery: !recoveryAlreadyArmed };
}

/** what the component does when one exercise's details could not be read for the day list */
export enum DayItemsExerciseReadFailureAction {
  /** an unexpected failure: report it to Sentry */
  Report = 'report',
  /** a dropped request: write a console line, no report */
  Log = 'log',
  /** the app shut its own database down (sign-out, say): not a fault, say nothing */
  Ignore = 'ignore',
}

/**
 * Decides how a failed read of one exercise's details is handled. Either way the day list
 * shows '?' for that one exercise's name (df-a89f1ea2145e).
 *
 * df-b0163737f620: a dropped request is logged, not reported. While the catalog is still
 * downloading, the read goes to the server, so a moment without network fails it, and every
 * such report from real users was noise (Sentry CYBERNETIC-FITNESS-APP-4NH). This is the
 * same choice as decideDayItemsLoadFailure makes for the schedule load (#6162).
 */
export function decideDayItemsExerciseReadFailure(err: unknown): DayItemsExerciseReadFailureAction {
  if (isDatabaseTornDown(err)) return DayItemsExerciseReadFailureAction.Ignore;
  if (isTransientFetchError(err)) return DayItemsExerciseReadFailureAction.Log;
  return DayItemsExerciseReadFailureAction.Report;
}

export interface DeferredDayItemsReloadDeps {
  /**
   * Subscribes to the database's initial-sync outcomes — normally
   * `listener => database.onInitialSyncSettled(listener)`. If the sync is already done the
   * subscription answers straight away, so the recovery degrades to one immediate retry
   * (which then reads local data).
   *
   * A subscription rather than the database's `whenInitialSyncCompleted` promise: the retry
   * loops replace that field once per round, so a caller holding one of its promises is
   * never told about the round that finally settles (#4435).
   */
  subscribeToInitialSyncSettled: InitialSyncSettledSubscribe;
  /** checked before each step so a destroyed component never reloads */
  isDestroyed: () => boolean;
  /** clears the backlog cache so the reload re-reads from the (now synced) local database */
  clearBacklogCacheAsync: () => Promise<void>;
  /**
   * runs the component's re-armed `reloadScheduleAsync` closure, then answers the outcome of
   * the NEWEST load, which may not be the reload's own: the user can change day while the
   * reload's read is in flight, and only the newest load decides what the Daily Log shows
   */
  reloadAsync: () => Promise<DayItemsLoadOutcome>;
  /** surfaces an unexpected error from the recovery itself (transient reload errors are handled inside reloadAsync) */
  trackAbnormalException: (err: unknown) => void;
  /** called when the reload built the Daily Log: the one point that proves the repair ran */
  onRecovered: () => void;
  /** called when the reload hit a dropped request again, so the Daily Log stays empty */
  onReloadFailedAgain: () => void;
  /** always called last — clears the component's "recovery armed" flag */
  onDone: () => void;
}

/**
 * The one-shot deferred reload armed by decideDayItemsLoadFailure: waits until the
 * initial sync settles, then clears the backlog cache and rebuilds the schedule from
 * the local database, and reports which way that went (onRecovered / onReloadFailedAgain).
 * Never throws; unexpected errors are reported and onDone always runs.
 */
export async function runDeferredDayItemsReloadAsync(deps: DeferredDayItemsReloadDeps): Promise<void> {
  try {
    // wait until the initial sync settles. even a sync that settles as failed (offline
    // exhaustion) is worth a reload: showing whatever data is local beats an empty page,
    // and so is giving up at the cap, which is why the wait is capped at all. why this is
    // not an await on the wrapper's promise: see waitForInitialSyncAsync and
    // design-docs/sync-integrity.md.
    await waitForInitialSyncAsync(deps.subscribeToInitialSyncSettled, InitialSyncWaitFor.AnyOutcome);
    if (deps.isDestroyed()) return;

    // the backlog cache may hold results from before the failure — clear it so the reload
    // re-reads from the local database the sync just filled
    await deps.clearBacklogCacheAsync();
    if (deps.isDestroyed()) return;

    const outcome = await deps.reloadAsync();
    // if the newest load built the Daily Log, the repair is proven; if it hit a dropped
    // request, nothing else will rebuild it, so the empty Daily Log must be reported; if a
    // newer load is still running, it decides, and it arms its own recovery if it fails
    // (this one clears the armed flag in onDone below)
    if (outcome === DayItemsLoadOutcome.Loaded) {
      deps.onRecovered();
    } else if (outcome === DayItemsLoadOutcome.DeferredAfterDroppedRequest) {
      deps.onReloadFailedAgain();
    }
  } catch (err) {
    // the reload's own failure handling covers transient errors, so anything that reaches
    // this catch is unexpected and must surface
    deps.trackAbnormalException(err);
  } finally {
    deps.onDone();
  }
}
